import { MatDialog } from '@angular/material/dialog';
import { TestErrorService } from './gt2/api/test-error.service';
import { Platform } from '@angular/cdk/platform';
import { CommonModule, DOCUMENT } from '@angular/common';
import {
  AfterViewInit,
  Component,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  NavigationEnd,
  Router,
  NavigationStart,
  NavigationError,
  ActivatedRoute,
} from '@angular/router';
import { FuseNavigationService } from '../@fuse/components/navigation/navigation.service';
import { FuseSidebarService } from '../@fuse/components/sidebar/sidebar.service';
import { FuseConfigService } from '../@fuse/services/config.service';
import { FuseSplashScreenService } from '../@fuse/services/splash-screen.service';
import { FuseTranslationLoaderService } from '../@fuse/services/translation-loader.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { navigation, navigationEn } from './navigation/navigation';
import { BROWSERS, DeviceDetectorService } from 'ngx-device-detector';
import { NGXLogger } from 'ngx-logger';
import { NgxPermissionsService } from 'ngx-permissions';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AuthenticationService } from './gt2/api/auth-service.service';
import { EmployeeModelData } from './gt2/api/models/CrudEmployeeModel';
import { UserSettingsService } from './gt2/api/user-settings.service';
import { TopAlertModel } from './gt2/components/top-alerts/top-alerts.component';
import { AppRoutes } from './gt2/const/AppRoutes';
import { GT2ConstantsEnum } from './gt2/const/GT2ConstantsEnum';
import { MENU_CAPABILITY } from './gt2/const/Permissions';
import { GT2PageAbstract } from './gt2/content/abstract/GT2PageAbstract';
import { CrudEmployeeService } from './gt2/content/crud/crud-employee/crud-employee.service';
import { ChatStore } from './services/chat/chat-store.service';
import { CrudOrganizationService } from './gt2/content/crud/crud-organization/crud-organization.service';
import { JwtInterceptor } from './gt2/interceptors/JwtInterceptor';
import { rewriteUrlWithTenantPrefix } from './gt2/gards/tenant-prefix.guard';
import { ValidatorHelper } from './gt2/validators/ValidatorHelper';
import { locale as genericEnglish } from './localization/i18n/en-CA';
import { locale as genericFrench } from './localization/i18n/fr-CA';
import {
  LanguageModel,
  LocalizationConfig,
} from './localization/LocalizationConfig';
import { locale as navigationEnglish } from './navigation/i18n/en-CA';
import { locale as navigationFrench } from './navigation/i18n/fr-CA';
import { DevGroupNavigationModel } from './navigation/restricted-group/dev-group.navigation.model';
import { GodGroupNavigationModel } from './navigation/restricted-group/god-group.navigation.model';
import { ModulesService } from './gt2/services/modules.service';
import { SearchService } from './gt2/api/search.service';
import { SubmissionsBadgeService } from './gt2/services/submissions-badge.service';
import {
  UpgradeFeatureKey,
  UpgradePanelService,
} from './gt2/services/upgrade-panel.service';
import { Title } from '@angular/platform-browser';
import { RolesService } from './gt2/services/roles.service';
import { NavigationFactory } from './navigation/NavigationFactory';
import { Gt2ApiService } from './gt2/api/gt2-api.service';
import { TestErrorResultComponent } from './gt2/components/dialogs/test-error-result/test-error-result.component';
import { GtTools } from './gt2/directives/gt-clients.directive';
import { FuseProgressBarModule } from '../@fuse/components/progress-bar/progress-bar.module';
import { NgxSpinnerModule } from 'ngx-spinner';
import { FuseSidebarModule } from '../@fuse/components/sidebar/sidebar.module';
import { FuseThemeOptionsModule } from '../@fuse/components/theme-options/theme-options.module';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { FirebaseProcessComponent } from './gt2/components/firebase-process/firebase-process.component';
import { HorizontalLayout1Component } from './layout/horizontal/layout-1/layout-1.component';
import { VerticalLayout1Component } from './layout/vertical/layout-1/layout-1.component';
import { VerticalLayout2Component } from './layout/vertical/layout-2/layout-2.component';
import { VerticalLayout3Component } from './layout/vertical/layout-3/layout-3.component';
import { AlertCheckVersionComponent } from './gt2/components/dialogs/alert-check-version/alert-check-version.component';
import { environment } from '../environments/environment';
import { ExternalEnvService } from './gt2/api/external-env.service';
import { VersionCheckService } from './services/version-check.service';
import { MobileZoomService } from './gt2/services/mobile-zoom.service';
import { BellNotificationsService } from './gt2/services/bell-notifications.service';
import { Gt2AppFontPreviewService } from './gt2/services/gt2-app-font-preview.service';
import { ChatWidgetComponent } from './components/chat-widget/chat-widget.component';

@Component({
  selector: 'app',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FuseProgressBarModule,
    NgxSpinnerModule,
    MatButtonModule,
    MatInputModule,
    VerticalLayout1Component,
    VerticalLayout2Component,
    VerticalLayout3Component,
    TranslateModule,
    MatFormFieldModule,
    FormsModule,
    ReactiveFormsModule,
    HorizontalLayout1Component,
    FirebaseProcessComponent,
    FuseSidebarModule,
    FuseThemeOptionsModule,
    ChatWidgetComponent,
  ],
  providers: [ExternalEnvService],
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  fuseConfig: any;
  navigation: any;

  dialogRef: any;
  topAlerts: TopAlertModel[] = [];

  innerWidth: any;

  // @ViewChild('firebaseProcessComponent') firebaseProcessComponent: FirebaseProcessComponent;

  get hasAuthToken(): boolean {
    return !!this.auth.getToken();
  }

  private readonly mobileZoomService = inject(MobileZoomService);

  /**
   * Bootstrap global du chat (T6) : initialise le ChatStore hors module chat
   * pour que le badge unread de la navigation vive dès le login. Gate GOD
   * (le chat est GOD-only en V1); idempotent (le module réutilise le store).
   */
  private bootstrapChatBadge(): void {
    const tryInit = (me: any): void => {
      if (!me?.user?.uuid) {
        return;
      }
      this.permissionsService.hasPermission('GOD').then((isGod) => {
        if (isGod) {
          this.chatStore.init({
            uuid: me.user.uuid,
            name:
              me.full_name ??
              `${me.first_name ?? ''} ${me.last_name ?? ''}`.trim(),
            avatar_thumb: me.avatar_thumb ?? null,
          });
        }
      });
    };

    tryInit(this.employeeService.me);
    this.employeeService.getMe().subscribe({ next: (me: any) => tryInit(me) });
  }

  // Private
  private _unsubscribeAll: Subject<any>;
  private _bottomSheetRef: any;

  /*
   * Constructor
   *
   * @param {DOCUMENT} document
   * @param {FuseConfigService} _fuseConfigService
   * @param {FuseNavigationService} _fuseNavigationService
   * @param {FuseSidebarService} _fuseSidebarService
   * @param {FuseSplashScreenService} _fuseSplashScreenService
   * @param {FuseTranslationLoaderService} _fuseTranslationLoaderService
   * @param {Platform} _platform
   * @param {TranslateService} _translateService
   * @param {AuthenticationService} auth
   * @param {Router} router
   * @param {MatDialog} dialog
   * @param {CrudEmployeeService} employeeService
   * @param {CrudOrganizationService} orgService
   * @param {NgxPermissionsService} permissionsService
   */
  constructor(
    @Inject(DOCUMENT) private document: any,
    private _fuseConfigService: FuseConfigService,
    private _fuseNavigationService: FuseNavigationService,
    private _fuseSidebarService: FuseSidebarService,
    private _fuseSplashScreenService: FuseSplashScreenService,
    private _fuseTranslationLoaderService: FuseTranslationLoaderService,
    private _translateService: TranslateService,
    private _platform: Platform,
    private auth: AuthenticationService,
    private externalEnvService: ExternalEnvService,
    private router: Router,
    private employeeService: CrudEmployeeService,
    private chatStore: ChatStore,
    private orgService: CrudOrganizationService,
    private permissionsService: NgxPermissionsService,
    private rolesService: RolesService,
    public userSettingsService: UserSettingsService,
    private logger: NGXLogger,
    private titleService: Title,
    private translate: TranslateService,
    public modulesService: ModulesService,
    private submissionsBadgeService: SubmissionsBadgeService,
    private deviceService: DeviceDetectorService,
    private api: Gt2ApiService,
    private testErrorService: TestErrorService,
    private dialog: MatDialog,
    private activeRoute: ActivatedRoute,
    private versionCheckService: VersionCheckService,
    private appFontPreview: Gt2AppFontPreviewService,
    // Centre de notifications : cloche + messages éclair en temps réel (Reverb).
    // Injecté ici pour vivre dès le démarrage ; remplace les toasts Firebase.
    private bellNotificationsService: BellNotificationsService,
    private upgradePanelService: UpgradePanelService,
  ) {
    // --o METRICS: Google Analytics, HotJar
    this.router.events.subscribe({
      next: (event) => {
        if (event instanceof NavigationEnd) {
          this.titleService.setTitle(this.getTitle(event.url));
          // this.logger.info("NavigationEnd -> Google Analytic tracking - url: " + event.urlAfterRedirects);
        }
        if (event instanceof NavigationStart) {
          // URL sans le nom du tenant qui matche par accident `:shop_uuid/<page>`
          // (ex. /configuration/rapports) : on la réécrit avec le tenant mémorisé.
          const rewritten = rewriteUrlWithTenantPrefix(event.url);
          if (rewritten) {
            this.router.navigateByUrl(rewritten, { replaceUrl: true });
          }
        }
      },
    });
    //
    // init
    GtTools.client.init(modulesService);

    // Add languages
    this._translateService.addLangs(['en-CA', 'fr-CA']);

    // Set the default language
    // this._translateService.setDefaultLang('en');
    this._translateService.setDefaultLang(LocalizationConfig.DEFAULT_LANGUAGE);

    // Set the navigation translations
    // Set the navigation and generic translations
    this._fuseTranslationLoaderService.loadTranslations(
      navigationEnglish,
      navigationFrench,
    );
    this._fuseTranslationLoaderService.loadTranslations(
      genericEnglish,
      genericFrench,
    );

    // Use a language
    const langModel: LanguageModel = LocalizationConfig.getCurrentLanguage();
    this._translateService.use(langModel.id);

    // Get default navigation copy
    if (langModel.id !== 'fr-CA') {
      this.navigation = JSON.parse(JSON.stringify(navigationEn));
    } else {
      this.navigation = JSON.parse(JSON.stringify(navigation));
    }

    // Register the navigation to the service
    this._fuseNavigationService.register('main', this.navigation);

    // Set the main navigation as our current navigation
    this._fuseNavigationService.setCurrentNavigation('main');

    // Add is-mobile class to the body if the platform is mobile
    if (this._platform.ANDROID || this._platform.IOS) {
      // Keeping this line just in case.
      this.document.body.classList.add('is-mobile');
    }

    // Set the private defaults
    this._unsubscribeAll = new Subject();

    //
    const isMobile: boolean = this.deviceService.isMobile();
    JwtInterceptor.IS_MOBILE = isMobile;

    const browser: string = this.deviceService.browser.toLowerCase();
    const browserIsSupported: boolean =
      browser !== BROWSERS.IE && browser !== BROWSERS.OPERA;
    JwtInterceptor.BROSWER_SUPPORTED = browserIsSupported;
    // this.logger.info("BROWSER: " + browser);
    this.innerWidth = window.innerWidth;

    JwtInterceptor.IS_MOBILE = false;
    if (this.auth.getToken()) {
      this.setUpGT2App();
    }

    /*if (isMobile && this.innerWidth < 800) {
      // this.logger.info("PLATFORM: IS MOBILE");
      // this.logger.info(window.innerWidth);
      if (this.api.isDemo === false) {
        this.router.navigateByUrl("/" + AppRoutes.DEVICE_NOT_SUPPORTED);
      }
      // this._fuseSplashScreenService.hide();
    } else if (!browserIsSupported) {
      //this.logger.info("BROWSER NOT SUPPORTED");
      this.router.navigateByUrl(AppRoutes.BROWSER_NOT_SUPPORTED);
      // this._fuseSplashScreenService.hide();
    } else {
      JwtInterceptor.IS_MOBILE = false;
      // this.logger.info("PLATFORM: IS NOT MOBILE (I REPEAT, NOT MOBILE)");
      // this.getExternalEnv();
      this.setUpGT2App();
    }*/

    //
    this.testErrorService.onTestFromRequest.subscribe({
      next: (testFromRequestResult) => {
        if (testFromRequestResult) {
          /* this.logger.info(
                        "AppComponent.constructor().testErrorService() -> testFromRequestResult:  " +
                            JSON.stringify(testFromRequestResult)
                    ); */

          //
          this.dialogRef = this.dialog.open(TestErrorResultComponent, {
            width: '96vw',
            maxHeight: '98vh',
            minWidth: 300,
            disableClose: false,
            panelClass: 'scrollable-dialog',
            data: {
              testFromRequestResult: testFromRequestResult,
            },
          });

          this.dialogRef.afterClosed().subscribe({
            next: (result: any) => {
              if (result) {
                // this.logger.info("POP UP Closed!");
              }
              this.dialogRef = null;
            },
          });
        }
      },
    });
  }

  public setUpGT2App(): void {
    this.employeeService.employee.subscribe({
      next: (employee: any) => {
        if (employee) {
          // top_alert removed from employee endpoint — now comes from organization

          // override the current to use of LocalizationConfig.getCurrentLanguage() once the employee is loaded.
          // The employee local set in the Database has the priority.
          if (employee.employee_locale) {
            this._translateService.use(employee.employee_locale);
          }

          // Permissions par rôle : le store ngx-permissions reçoit le nom du rôle
          // (gardes GOD-only, isGodUser()) ET la liste effective des capacités
          // calculée par l'API (`me.permissions`, voir gt2/const/Permissions.ts).
          // Un SEUL loadPermissions : deux émissions créeraient un état
          // intermédiaire où toutes les gardes de capacité sont fausses.
          const perm: string[] = [
            employee.user.role,
            ...(employee.permissions ?? []),
          ];
          GT2PageAbstract.USER_ROLE = employee.user.role;
          this.permissionsService.loadPermissions(perm);
          const grantedCapabilities = new Set<string>(
            employee.permissions ?? [],
          );

          //
          const langModel: LanguageModel =
            LocalizationConfig.getCurrentLanguage();
          const navigation_menu = employee.navigation_menu.flatMap(
            (item: any) => {
              if (item.id === 'dashboard' && item.app_dasboard_modules) {
                const moduleKeys = Object.keys(item.app_dasboard_modules);
                const dashboardRelated = [
                  'DASHBOARD_EVENT_ORIGINAL',
                  'DASHBOARD_JUL',
                  'DASHBOARD_EVENTS',
                ];

                let dashboardCounter = 0;

                return moduleKeys
                  .map((key) => {
                    if (dashboardRelated.includes(key)) {
                      const id =
                        dashboardCounter === 0
                          ? 'dashboard'
                          : `dashboard-${dashboardCounter}`;
                      dashboardCounter++;
                      return { id };
                    } else if (key === 'DASHBOARD_OPERATIONS') {
                      return { id: 'operations' };
                    } else if (key === 'DASHBOARD_PERFORMANCE') {
                      return { id: 'performance' };
                    } else if (key === 'DASHBOARD_PERFORMANCE_EXPERT') {
                      return { id: 'performance-expert' };
                    } else {
                      return null; // ignore unknown keys
                    }
                  })
                  .filter(Boolean); // remove nulls
              }
              return [item];
            },
          );

          if (langModel.id !== 'fr-CA') {
            const nav = NavigationFactory.create(navigationEn, navigation_menu);
            const navWithTrad = nav.map((n: any) =>
              n?.id !== 'applications'
                ? n
                : n?.children && Array.isArray(n?.children)
                  ? {
                      ...n,
                      children: n.children.map((c: any) =>
                        ![
                          'dashboard',
                          'dashboard-1',
                          'dashboard-2',
                          'dashboard-3',
                          'dashboard-4',
                          'operations',
                          'performance',
                          'performance-expert',
                        ].includes(c?.id)
                          ? c
                          : {
                              ...c,
                              title: this.getDashboardModuleFromMenu(
                                employee.navigation_menu,
                                c?.id,
                              )?.['en-CA'],
                            },
                      ),
                    }
                  : n,
            );
            this.navigation = navWithTrad;
          } else {
            const nav = NavigationFactory.create(navigation, navigation_menu);
            const navWithTrad = nav.map((n: any) =>
              n?.id !== 'applications'
                ? n
                : n?.children && Array.isArray(n?.children)
                  ? {
                      ...n,
                      children: n.children.map((c: any) =>
                        ![
                          'dashboard',
                          'dashboard-1',
                          'dashboard-2',
                          'dashboard-3',
                          'dashboard-4',
                          'operations',
                          'performance',
                          'performance-expert',
                        ].includes(c?.id)
                          ? c
                          : {
                              ...c,
                              title: this.getDashboardModuleFromMenu(
                                employee.navigation_menu,
                                c?.id,
                              )?.['fr-CA'],
                            },
                      ),
                    }
                  : n,
            );
            this.navigation = navWithTrad;
          }

          // RMS magic links: inject dynamic URL or hide item
          const platformItem = this.navigation[0]?.children?.find(
            (c: any) => c.id === 'platform',
          );
          if (platformItem?.children) {
            const magicLinks: { id: string; field: string }[] = [
              { id: 'rms-magic-link', field: 'rms-magic-link' },
              {
                id: 'rms-magic-link-livraison',
                field: 'rms-magic-link-livraison',
              },
              { id: 'roadmap-magic-link', field: 'roadmap-magic-link' },
            ];
            for (const ml of magicLinks) {
              const navItem = platformItem.children.find(
                (c: any) => c.id === ml.id,
              );
              if (navItem) {
                const url = employee[ml.field];
                if (url) {
                  navItem.url = url;
                } else {
                  navItem.hidden = true;
                }
              }
            }
          }

          // Menu gauche : l'API fournit navigation_menu (modules) — premier filtre,
          // inchangé (NavigationFactory). Deuxième filtre : capacités MENU_* de la
          // matrice « Permissions par rôle » (défauts = ancienne cascade par rôle).
          // Une entrée absente de MENU_CAPABILITY n'est pas filtrée ici. La règle
          // CT (Rapports réservé aux OWNER) est appliquée par l'API sur MENU_REPORTS.
          // Un `permissions` absent (ancienne API) = aucun filtrage par capacité.
          if (Array.isArray(employee.permissions)) {
            this.navigation[0].children = this.navigation[0].children.filter(
              (item: any) =>
                !MENU_CAPABILITY[item.id] ||
                grantedCapabilities.has(MENU_CAPABILITY[item.id]),
            );
          }

          if (employee.user.role === GT2ConstantsEnum.GOD) {
            this.navigation.push(new GodGroupNavigationModel());
            //this.navigation.push(new DevGroupNavigationModel());
          }

          // Mise à niveau (paywall) : l'API n'inclut le menu « upgrade » que pour un tenant
          // au forfait ESSENTIEL (GOD-only pendant le développement). Les sous-menus
          // « panneau » n'ont pas d'URL : on leur rattache ici leur fonction, perdue au
          // clone JSON de la navigation.
          const upgradeItem = this.navigation[0]?.children?.find(
            (c: any) => c.id === 'upgrade',
          );
          if (upgradeItem?.children) {
            const panelFeatures: Record<string, UpgradeFeatureKey> = {
              'upgrade-features': 'features',
              'upgrade-submissions': 'submissions',
              'upgrade-automations': 'automations',
              'upgrade-integrations': 'integrations',
              // 'upgrade-ai': 'ai', // IA : sera ajoutée en V2 du paywall
            };
            upgradeItem.children.forEach((child: any) => {
              const feature = panelFeatures[child.id];
              if (feature) {
                child.function = () =>
                  this.upgradePanelService.open({ feature }).subscribe();
              }
            });
          }

          // Register the navigation to the service
          this._fuseNavigationService.unregister('main');
          this._fuseNavigationService.register('main', this.navigation);
          this._fuseNavigationService.setCurrentNavigation('main');

          // --o reset language
          this._translateService.setDefaultLang(
            LocalizationConfig.DEFAULT_LANGUAGE,
          );
          this._translateService.use(
            LocalizationConfig.getCurrentLanguage().id,
          );
          //
          this.loadStartingData(employee);

          // this.logger.info("app.component -> 1. ***** HIDE SPLASH SCREEN *****");
          this._fuseSplashScreenService.hide();
        }
      },
    });
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    // « Taille d'affichage sur mobile » (Préférences) : échelle du viewport, téléphone seulement
    this.mobileZoomService.apply();

    this.bootstrapChatBadge();

    // Strip the cache-buster query param left behind by a stale-chunk recovery
    // reload, so it doesn't linger in the address bar.
    if (
      typeof window !== 'undefined' &&
      window.location.search.includes('_cb=')
    ) {
      const cleanUrl = new URL(window.location.href);
      cleanUrl.searchParams.delete('_cb');
      window.history.replaceState(
        {},
        '',
        cleanUrl.pathname + cleanUrl.search + cleanUrl.hash,
      );
    }

    // Start checking for new versions
    // Ensure environment.production is true for production builds to use the correct path
    const versionFileUrl = 'assets/version.json';
    this.versionCheckService.startVersionCheck(versionFileUrl, 5 * 60 * 1000); // Check every 5 minutes

    // If using Angular Service Worker (PWA)
    this.versionCheckService.initServiceWorkerUpdateListener();

    // Recover from "stale lazy chunk" failures after a deploy: when an old tab
    // navigates to a lazy route whose chunk no longer exists, the dynamic import
    // fails (MIME/text-html) and the screen stays blank. Reload onto the new
    // build instead. Covered both at the router level and globally.
    this.versionCheckService.registerChunkErrorHandler();
    this.router.events
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((event) => {
        if (
          event instanceof NavigationError &&
          VersionCheckService.isChunkLoadError(event.error)
        ) {
          this.versionCheckService.reloadForBrokenChunk();
        }
      });

    // Replace the native minLength validator with a custom one (also validate string with trim())
    Validators.minLength = ValidatorHelper.minLength;

    // Subscribe to config changes
    this._fuseConfigService.config
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe({
        next: (config: any) => {
          this.fuseConfig = config;

          // Boxed
          if (this.fuseConfig.layout.width === 'boxed') {
            this.document.body.classList.add('boxed');
          } else {
            this.document.body.classList.remove('boxed');
          }

          // Color theme - Use normal for loop for IE11 compatibility
          for (let i = 0; i < this.document.body.classList.length; i++) {
            const className = this.document.body.classList[i];

            if (className.startsWith('theme-')) {
              this.document.body.classList.remove(className);
            }
          }
          let colorCache = localStorage.getItem('theme');
          let currentTheme =
            colorCache === 'theme-blue-gray-dark' ||
            colorCache === 'theme-default'
              ? colorCache
              : 'theme-default';
          localStorage.setItem('theme', currentTheme);

          this.document.body.classList.add(currentTheme);
          if (currentTheme === 'theme-blue-gray-dark') {
            this.document.body.classList.add('dark');
          } else {
            this.document.body.classList.remove('dark');
          }
        },
      });

    this.auth.logoutSubject.subscribe({
      next: (value) => {
        this.topAlerts = [];
        GT2PageAbstract.USER_ROLE = '';
        // Vide rôle + capacités : la prochaine session recharge sa propre liste.
        this.permissionsService.flushPermissions();
      },
    });

    const theme = localStorage.getItem('theme') || 'theme-default';
    document.body.className = theme;
    if (theme === 'theme-blue-gray-dark') {
      document.body.classList.add('dark');
    } else {
      document.body.classList.remove('dark');
    }
    // Désactivé : la police par défaut est Montserrat (voir _typography.scss).
    // Conservé en commentaire pour pouvoir réactiver l'aperçu temps réel en mode God.
    // this.appFontPreview.syncBody();
  }

  ngAfterViewInit(): void {
    //this.initGoogleAnalyticsPageView();
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    this.versionCheckService.stopVersionCheck();
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(undefined);
    this._unsubscribeAll.complete();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  @HostListener('window:keydown.control.k', ['$event'])
  openSearch(event: Event): void {
    event.preventDefault();
  }
  /**
   * Toggle sidebar open
   *
   * @param key
   */
  toggleSidebarOpen(key: any): void {
    this._fuseSidebarService.getSidebar(key).toggleOpen();
  }

  public loadStartingData(employee: EmployeeModelData): void {
    const organizationMe = this.orgService.getOrganizationMeObject();
    if (organizationMe === null) {
      this.orgService
        .loadOrganizationMe('App.Component.loadStartingData')
        .subscribe({
          next: (organizations) => {
            this.modulesService.setModules(organizations.data.appModules);
            SearchService.setServerEngine(organizations.data?.search_engine);
            this.topAlerts = organizations.data?.caterer?.top_alerts || [];
          },
        });
    } else {
      this.modulesService.setModules(organizationMe.data.appModules);
      SearchService.setServerEngine(organizationMe.data?.search_engine);
      this.topAlerts = organizationMe.data?.caterer?.top_alerts || [];
    }

    this.modulesService.modulesSet.subscribe({
      next: (data) => {
        if (data) {
          // REMOVE BUTTONS FROM MENU IF THE ORG DOES NOT HAVE THE CORRESPONDANT MODULES
          if (!this.modulesService.hasShopWeb()) {
            const newNav = this.navigation[0].children.filter((item: any) => {
              return item.id !== 'shops';
            });
            this.navigation[0].children = newNav;
          }

          if (!this.modulesService.hasSubmissions()) {
            const newNav = this.navigation[0].children.filter((item: any) => {
              return item.id !== 'submissions';
            });
            this.navigation[0].children = newNav;
          }
          // Courriels envoyés (sous-menu God) : GOD seulement, et le module reste requis.
          if (!this.modulesService.hasEmailHistory()) {
            const godMenu = this.navigation.find(
              (item: any) => item.id === 'godMenu',
            );
            if (godMenu?.children) {
              godMenu.children = godMenu.children.filter((item: any) => {
                return item.id !== 'sent-emails';
              });
            }
          }
          // Puce « Soumissions » (soumissions web en attente) — no-op sans le module.
          this.submissionsBadgeService.start();
          //
          // if is JUL and were on the main dashboard
          // redirect to JUL dashboard
          // if (this.modulesService.hasJULDashboard()) {
          //   if (
          //     this.router.url === '/tableau-de-bord' ||
          //     this.router.url === '/dashboard'
          //   ) {
          //     this.router.navigateByUrl(AppRoutes.EVENTS);
          //   }
          // }

          if (!this.modulesService.hasRoomManagement()) {
            const newNav = this.navigation[0].children.filter((item: any) => {
              return item.id !== 'room-management';
            });
            this.navigation[0].children = newNav;
            //
            this._fuseNavigationService.unregister('main');
            this._fuseNavigationService.register('main', this.navigation);
            this._fuseNavigationService.setCurrentNavigation('main');
          }
        }
      },
    });

    this.userSettingsService.getAppPreferences().subscribe({
      next: (response: any) => {
        // this.logger.info('AppComponent.loadStartingData() -> SUCCESS: ' + JSON.stringify(response));
        // this.userSettingsService.setUserSettings(response.data);
        // localStorage.setItem(DateFormatPreference.DATE_FORMAT_KEY, null);
      },
      error: (error: any) => {
        this.logger.error(
          'AppComponent.loadStartingData() -> userSettingsService ERRORS: ' +
            JSON.stringify(error),
        );
      },
    });
  }
  getTitle(url: string) {
    const lang =
      localStorage.getItem(LocalizationConfig.LOCAL_STORED_LANG_ID) || 'fr-CA';
    const envTag =
      environment.firebaseEnvironment === 'dev'
        ? '[DEV] '
        : environment.firebaseEnvironment === 'staging'
          ? '[STG] '
          : '';
    const prefix = envTag + (lang === 'en-CA' ? 'CM - ' : 'GT - ');

    const fromUrlToTitle = [
      {
        url: '/configuration/categories-clients',
        title: 'Configuration - Catégories de clients',
      },
      {
        url: '/configuration/categories-materiels',
        title: 'Configuration - Catégories de matériels',
      },
      {
        url: '/configuration/categories-produits',
        title: 'Configuration - Catégories de produits',
      },
      {
        url: '/configuration/zone-de-production',
        title: 'Configuration - Zones de production',
      },
      {
        url: '/configuration/types-evenements',
        title: "Configuration - Types d'événements",
      },
      {
        url: '/configuration/allergenes',
        title: 'Configuration - Allergènes alimentaires',
      },
      {
        url: '/configuration/fonction-de-travail',
        title: 'Configuration - Fonctions de travail',
      },
      {
        url: '/configuration/fournisseur-materiel',
        title: 'Configuration - Fournisseurs de matériel',
      },
      {
        url: '/configuration/fournisseur-ressources-humaine',
        title: 'Configuration - Nom du fournisseur de ressources humaines',
      },
      {
        url: '/configuration/frais-de-service',
        title: 'Configuration - Frais de service',
      },
      {
        url: '/configuration/livraison-secteur',
        title: 'Configuration - Secteurs de livraison',
      },
      {
        url: '/configuration/model-demande-livraison',
        title: 'Configuration - Modèle de livraison',
      },
      {
        url: '/configuration/date-et-heure',
        title: 'Configuration - Date et heure',
      },
      {
        url: '/configuration/numerotation',
        title: 'Configuration - Numérotation',
      },
      {
        url: '/configuration/emballage-defaut',
        title: 'Configuration - Emballages par défaut',
      },
      { url: '/configuration/emballage', title: 'Configuration - Emballages' },
      { url: '/configuration/taxes', title: 'Configuration - Numéros de taxe' },
      {
        url: '/configuration/taxes-defaut',
        title: 'Configuration - Taxes par défaut',
      },
      {
        url: '/configuration/compte-de-grand-livre-defaut',
        title: 'Configuration - Compte de grand livre par défaut',
      },
      {
        url: '/configuration/compte-de-grand-livre',
        title: 'Configuration - Comptes de grand livre',
      },
      {
        url: '/configuration/conditions-generales',
        title: 'Configuration - Conditions générales',
      },
      {
        url: '/configuration/conditions-specifiques',
        title: 'Configuration - Conditions spécifiques',
      },
      {
        url: '/configuration/messages-notification-proposition',
        title: 'Configuration - Proposition',
      },
      {
        url: '/configuration/messages-notification-commande',
        title: 'Configuration - Commande',
      },
      {
        url: '/configuration/messages-notification-facture',
        title: 'Configuration - Facture',
      },
      {
        url: '/configuration/messages-notification-recu',
        title: 'Configuration - Reçu',
      },
      {
        url: '/configuration/messages-notification-etat-de-compte-client',
        title: 'Configuration - État de compte client',
      },
      {
        url: '/configuration/messages-notification-etat-de-compte-entreprise',
        title: 'Configuration - État de compte entreprise',
      },
      {
        url: '/configuration/messages-notification-horaire',
        title: 'Configuration - Horaire',
      },
      {
        url: '/configuration/notification_all_receipt_notices',
        title: 'Configuration - Avis de réception',
      },
      {
        url: '/configuration/user-employee-setting',
        title: "Configuration - Nombre d'utilisateurs",
      },

      {
        url: '/settings/categories-clients',
        title: 'Settings - Clients Categories',
      },
      {
        url: '/settings/categories-materiels',
        title: 'Settings - Equipment categories',
      },
      {
        url: '/settings/categories-produits',
        title: 'Settings - Products categories',
      },
      {
        url: '/settings/zone-de-production',
        title: 'Settings - Production zones',
      },
      { url: '/settings/types-evenements', title: 'Settings - Events types' },
      { url: '/settings/allergenes', title: 'Settings - Allergens' },
      {
        url: '/settings/fonction-de-travail',
        title: 'Settings - Job functions',
      },
      {
        url: '/settings/fournisseur-materiel',
        title: 'Settings - Equipments Providers',
      },
      {
        url: '/settings/fournisseur-ressources-humaine',
        title: "Settings - Provider's name",
      },
      {
        url: '/settings/frais-de-service',
        title: 'Settings - Service charges',
      },
      {
        url: '/settings/livraison-secteur',
        title: 'Settings - Delivery sectors',
      },
      {
        url: '/settings/model-demande-livraison',
        title: 'Settings - Delivery request templates',
      },
      { url: '/settings/date-et-heure', title: 'Settings - Date and time' },
      { url: '/settings/numerotation', title: 'Settings - Numbering' },
      {
        url: '/settings/emballage-defaut',
        title: 'Settings - Default wrapping',
      },
      { url: '/settings/emballage', title: 'Settings - Wrapping' },
      { url: '/settings/taxes', title: 'Settings - Taxes number' },
      { url: '/settings/taxes-defaut', title: 'Settings - Default taxes' },
      {
        url: '/settings/compte-de-grand-livre-defaut',
        title: 'Settings - Default ledger account',
      },
      {
        url: '/settings/compte-de-grand-livre',
        title: 'Settings - Ledger account',
      },
      {
        url: '/settings/conditions-generales',
        title: 'Settings - General conditions',
      },
      {
        url: '/settings/conditions-specifiques',
        title: 'Settings - Specific conditions',
      },
      {
        url: '/settings/messages-notification-proposition',
        title: 'Settings - Proposal',
      },
      {
        url: '/settings/messages-notification-commande',
        title: 'Settings - Order',
      },
      {
        url: '/settings/messages-notification-facture',
        title: 'Settings - Invoice',
      },
      {
        url: '/settings/messages-notification-recu',
        title: 'Settings - Receipt',
      },
      {
        url: '/settings/messages-notification-etat-de-compte-client',
        title: 'Settings - Client statement',
      },
      {
        url: '/settings/messages-notification-etat-de-compte-entreprise',
        title: 'Settings - Entreprise statement',
      },
      {
        url: '/settings/messages-notification-horaire',
        title: 'Settings - Schedule',
      },
      {
        url: '/settings/notification_all_receipt_notices',
        title: 'Settings - Receipt notice',
      },
      {
        url: '/settings/user-employee-setting',
        title: 'Settings - Number of users',
      },
      { url: '/clients/', title: 'Client' },
      {
        url: '/clients',
        title:
          lang === 'fr-CA'
            ? 'Clients et entreprises'
            : 'Clients and enterprises',
      },
      { url: '/entreprises/', title: 'Entreprise' },
      { url: '/entreprises', title: 'Entreprises' },
      { url: '/projet/', title: 'Projet' },
      { url: '/projet', title: 'Projets' },
      { url: '/congresses', title: lang === 'fr-CA' ? 'Congrès' : 'Congress' },
      { url: '/produits/', title: 'Produit' },
      { url: '/produits', title: 'Produits' },
      { url: '/materiels/', title: 'Matériel' },
      { url: '/materiels', title: 'Matériels' },
      { url: '/lieux/', title: 'Lieu' },
      { url: '/lieux', title: 'Lieux' },
      { url: '/employes/', title: 'Employé' },
      { url: '/employes', title: 'Employés' },
      { url: '/salles', title: 'Salle' },
      {
        url: '/configuration-generale',
        title: 'Boutiques - Configuration générale',
      },
      {
        url: '/multi-point-cueillette',
        title: 'Boutique - Multi-point de cueillette',
      },
      { url: '/moneris', title: 'Commande en ligne - Moneris' },
      { url: '/stripe', title: 'Commande en ligne - Stripe' },
      { url: '/global-payments', title: 'Commande en ligne - Global payment' },
      { url: '/theme', title: 'Commande en ligne - Thème' },
      {
        url: '/regles-livraison',
        title: 'Commande en ligne - Règles de livraison',
      },
      {
        url: '/generateur-horaire-commandes',
        title: 'Commande en ligne - Horaire des livraisons et cueillettes',
      },
      { url: '/adresse', title: 'Commande en ligne - Adresse' },
      {
        url: '/calendrier',
        title: 'Commande en ligne - Calendrier des disponibilités',
      },
      {
        url: '/heures-ouverture',
        title: 'Commande en ligne - Heures d’ouverture',
      },
      { url: '/pourboire', title: 'Commande en ligne - Pourboire' },
      {
        url: '/termes-conditions',
        title: 'Commande en ligne - Termes et conditions',
      },
      { url: '/notifications', title: 'Commande en ligne - Notification' },
      { url: '/messages', title: 'Commande en ligne - Messages' },
      { url: '/categories', title: 'Commande en ligne - Catégories' },
      { url: '/boutiques', title: 'Boutiques' },
      {
        url: '/affectations',
        title: lang === 'fr-CA' ? 'Affectations' : 'Assignments',
      },
      { url: '/evenements', title: 'Événement' },
      { url: '/commandes', title: 'Commande' },
      { url: '/propositions', title: 'Proposition' },
      { url: '/factures/', title: 'Facture' },
      { url: '/factures', title: 'Factures' },
      {
        url: '/errors/error-404',
        title: lang === 'fr-CA' ? 'Erreur 404' : 'Error 404',
      },
      {
        url: '/errors/error-500',
        title: lang === 'fr-CA' ? 'Erreur 500' : 'Error 500',
      },
      {
        url: '/errors/error-503',
        title: lang === 'fr-CA' ? 'Erreur 503' : 'Error 503',
      },
      {
        url: '/errors/error-429',
        title: lang === 'fr-CA' ? 'Erreur 429' : 'Error 429',
      },
      { url: '/god-reports', title: 'God Reports' },
      { url: '/authentification', title: 'Authentification' },
      { url: '/mot-de-passe', title: 'Mot de passe' },
      {
        url: '/operations',
        title: lang === 'fr-CA' ? 'Opérations' : 'Operations',
      },
      { url: '/performance', title: 'Performance' },
      { url: '/tableau-de-bord-jul', title: 'Tableau de bord Jul' },
      { url: '/tableau-de-bord', title: 'Tableau de bord' },
      {
        url: '/documentation',
        title: lang === 'fr-CA' ? 'Base de connaissance' : 'Knowledge base',
      },

      { url: '/advanced-dashboard', title: 'Advanced Dashboard' },
      { url: '/dashboard-jul', title: 'Dashboard Jul' },
      { url: '/dashboard', title: 'Dashboard' },
      { url: '/room-management', title: 'Room management' },
      { url: '/rooms/', title: 'Room' },
      { url: '/rooms', title: 'Rooms' },
      { url: '/companies/', title: 'Company' },
      { url: '/companies', title: 'Companies' },
      { url: '/products/', title: 'Product' },
      { url: '/products', title: 'Products' },
      { url: '/equipments/', title: 'Equipment' },
      { url: '/equipments', title: 'Equipments' },
      { url: '/places/', title: 'Place' },
      { url: '/places', title: 'Places' },
      { url: '/employees/', title: 'Employee' },
      { url: '/employees', title: 'Employees' },
      { url: '/shops', title: 'Shops' },
      {
        url: '/note-update',
        title: lang === 'fr-CA' ? 'Note update' : 'Release note',
      },
      { url: '/events/', title: 'Event' },
      { url: '/events', title: 'Events' },
      { url: '/orders/', title: 'Order' },
      { url: '/orders', title: 'Orders' },
      { url: '/proposals/', title: 'Proposal' },
      { url: '/proposals', title: 'Proposals' },
      { url: '/invoices/', title: 'Invoice' },
      { url: '/invoices', title: 'Invoices' },
      { url: '/caterer', title: 'Organization' },
      { url: '/password', title: 'Password' },
      { url: '/login', title: 'Login' },
      { url: '/projects/', title: 'Project' },
      { url: '/projects', title: 'Projects' },

      { url: '/sous-salles-2', title: 'Sous salles' },
      { url: '/sous-salles', title: 'Sous salles' },
      { url: '/produits-vente-incitative', title: 'Produits vente incitative' },
      { url: '/gestion-salles', title: 'Gestion des salles' },
      { url: '/base-de-connaissance', title: 'Base de connaissance' },
      { url: '/appareil-non-supporte', title: 'Erreur Appareil' },
      { url: '/browser-non-supporte', title: 'Erreur Navigateur' },
      { url: '/note-mise-a-jour', title: 'Note de mise à jour' },
      { url: '/god-emails', title: '' },
      { url: '/god-impersonation', title: '' },
      { url: '/god-local-storage', title: '' },
      { url: '/traiteur/organisation', title: '' },
      { url: '/employe/create', title: '' },
      { url: '/test', title: 'Test' },
      { url: '/profile', title: 'Profile' },
      { url: '/rapports', title: 'Rapports' },
      { url: '/support', title: 'Support' },
      { url: '/configuration', title: 'Configuration' },
      {
        url: '/preferences',
        title: lang === 'fr-CA' ? 'Préférences' : 'Preferences',
      },
      { url: '/recherche', title: 'Recherche' },
      { url: '/contact', title: 'Contact' },
      { url: '/gestion', title: 'Gestion' },
      { url: '/module1', title: 'Module1' },
      { url: '/maintenance', title: 'Maintenance' },
      { url: '/congres', title: 'Congrès' },
      { url: '/vente-incitative', title: 'Vente incitative' },
      { url: '/upsale', title: 'Upsale' },
      { url: '/fonction-de-travail', title: 'Fonction de travail' },
      { url: '/upsale-products', title: 'Upsale products' },
      { url: '/basement-rooms-2', title: 'Basement rooms' },
      { url: '/basement-rooms', title: 'Basement rooms' },
      { url: '/knowledge-base', title: 'Knowledge base' },
      { url: '/device-not-supported', title: 'Error device' },
      { url: '/browser-not-supported', title: 'Error browser' },
      { url: '/caterer/organization', title: '' },
      { url: '/employee/create', title: '' },
      { url: '/reports', title: 'Reports' },
      { url: '/settings', title: 'Settings' },
      { url: '/research', title: 'Research' },
      { url: '/management', title: 'Management' },
      { url: '/congress', title: 'Congress' },
      { url: '/traiteur', title: 'Organisation' },
    ];

    const title = fromUrlToTitle.find((t) => url.includes(t.url))?.title || '';

    return prefix + title;
  }
  getDashboardModuleFromMenu(
    navigation_menu: any[],
    id: string,
  ): { 'fr-CA': string; 'en-CA': string } | null {
    const dashboardEntry = navigation_menu.find(
      (item) => item.id === 'dashboard',
    );
    if (!dashboardEntry || !dashboardEntry.app_dasboard_modules) {
      return null;
    }

    const modules = dashboardEntry.app_dasboard_modules;

    // Fonction pour extraire uniquement fr-CA et en-CA
    const extractLangs = (
      module: any,
    ): { 'fr-CA': string; 'en-CA': string } | null => {
      if (module && module['fr-CA'] && module['en-CA']) {
        return {
          'fr-CA': module['fr-CA'],
          'en-CA': module['en-CA'],
        };
      }
      return null;
    };

    // Si l'id est 'performance' ou 'operations'
    if (id === 'performance') {
      return extractLangs(modules['DASHBOARD_PERFORMANCE']);
    } else if (id === 'performance-expert') {
      return extractLangs(modules['DASHBOARD_PERFORMANCE_EXPERT']);
    } else if (id === 'operations') {
      return extractLangs(modules['DASHBOARD_OPERATIONS']);
    }

    // dashboard, dashboard-1, ..., dashboard-4
    const match = id.match(/^dashboard(?:-(\d))?$/);
    if (match) {
      const index = match[1] ? parseInt(match[1], 10) : 0;

      // Exclure operations et performance
      const filteredModules = Object.entries(modules).filter(
        ([key]) =>
          key !== 'DASHBOARD_PERFORMANCE' &&
          key !== 'DASHBOARD_PERFORMANCE_EXPERT' &&
          key !== 'DASHBOARD_OPERATIONS',
      );

      const key = filteredModules[index]?.[0];
      return key ? extractLangs(modules[key]) : null;
    }

    return null;
  }

  // -----------------------------------------------------------------------------------------------------
  // @ PRIVATE methods
  // -----------------------------------------------------------------------------------------------------
  private initGoogleAnalyticsPageView(): void {
    const interval = setInterval(() => {
      if ((window as any).ga && (window as any).ga.getAll) {
        this.router.events.subscribe({
          next: (event) => {
            const ga = (window as any).ga;
            if (event instanceof NavigationEnd) {
              const tracker = ga.getAll()[0];
              tracker.set('page', event.urlAfterRedirects);
              tracker.send('pageview');
            }
          },
        });
        clearInterval(interval);
      }
    }, 50);
  }
}
